import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	inject,
	input,
	output,
	ViewEncapsulation,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';

import { LinkComponent } from '@valk-nx/components/ui-link/src/lib/link';
import type { Link } from '@valk-nx/components/ui-link/src/lib/link.interface';
import type { Language } from '@valk-nx/core/lib/core';
import { LANGUAGE } from '@valk-nx/core/lib/core';
import { BaseDirective } from '@valk-nx/core/lib/directives/base/base.directive';
import { WhatsappPipe } from '@valk-nx/core/lib/pipes/whatsapp/whatsapp.pipe';
import { GpsDirectionHelper } from '@valk-nx/helpers/lib/gps-direction/gps-direction.helper';
import { SVG_ICON_IMPORTS } from '@valk-nx/helpers/lib/svg-unique-ids/svg-unique-ids.directive';

import type {
	FooterCompanyInformation,
	FooterInformation,
	FooterMenu,
	FooterMenuLink,
	FooterUSP,
} from './footer.interface';

@Component({
	encapsulation: ViewEncapsulation.None,
	selector: 'vp-footer',
	templateUrl: './footer.html',
	imports: [
		CommonModule,
		LinkComponent,
		SVG_ICON_IMPORTS,
		WhatsappPipe,
		RouterModule,
		TranslatePipe,
	],
})
export class FooterComponent extends BaseDirective {
	private readonly rootLanguage = inject<Language>(LANGUAGE);

	bottomItems = input<FooterMenuLink[] | undefined>(undefined);
	information = input<FooterInformation | undefined>(undefined);
	company = input<FooterCompanyInformation | undefined>(undefined);
	isMinimal = input<boolean>(false);
	title = input<string>('');
	menu = input<FooterMenu>({
		items: [
			{
				title: '',
				items: [
					{
						title: '',
						url: '',
					},
				],
			},
		],
	});
	usps = input<FooterUSP[]>([
		{
			icon: 'assets/icons/logo-valk-white.svg',
			text: '',
			url: '',
		},
		{
			icon: 'assets/icons/logo-valk-white.svg',
			text: 'Van der Valk Hotel Group',
			url: '',
		},
	]);

	socials = input<{
		facebook?: string;
		instagram?: string;
		tiktok?: string;
		youtube?: string;
		linkedin?: string;
		pinterest?: string;
		whatsapp?: string;
	} | null>({
		facebook: 'https://www.facebook.com/VanderValkVerrast',
		instagram: 'https://www.instagram.com/valkverrast',
		tiktok: undefined,
		youtube: undefined,
		linkedin: undefined,
		pinterest: undefined,
		whatsapp: undefined,
	});

	toTop = output<void>();

	transformedTitle = computed(() =>
		this.title().toLowerCase().trim().replaceAll(/\s+/g, '-'),
	);

	logoLink = computed<Link | undefined>(() => ({
		url:
			this.language() === this.rootLanguage ? '/' : `/${this.language()}`,
		title: 'home',
	}));

	showContactSection = computed(
		() =>
			!!this.information()?.contact ||
			!!(
				this.company()?.name ||
				this.company()?.registrationNumber ||
				this.company()?.vatNumber
			),
	);

	planRouteUrl = computed<string | null>(() => {
		const address = this.information()?.address;

		if (!address) {
			return null;
		}

		return GpsDirectionHelper.getDirectionUrl(address);
	});

	constructor() {
		super();
	}

	scrollToTop(): void {
		this.toTop.emit();
		window.scrollTo({
			behavior: 'smooth',
			left: 0,
			top: 0,
		});
	}
}
